import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, GraduationCap, Building2, FileCheck2, Coffee, Video, Briefcase, MessageSquare, User, Pencil, X, Camera, Loader2 } from "lucide-react";
import { fmtDate } from "@/lib/dates";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DISCIPLINES = ["PT", "PTA", "OT", "OTA", "SLP", "Other"];
const CAN_EDIT_ROLES = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor"];

function PhotoAvatar({ student, canEdit, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  const token = typeof window !== "undefined" ? localStorage.getItem("cc_token") : null;
  const hasPhoto = !!student.photo_path;
  const src = hasPhoto ? `${BACKEND}/api/students/${student.id}/photo?auth=${token}&v=${ver}` : null;
  const initials = (student.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/students/${student.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated"); setVer((v) => v + 1); onUploaded?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the photo"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="flex items-center gap-4 mb-4" data-testid="sp-photo">
      <div className="relative">
        {src ? (
          <img src={src} alt={student.name} className="w-20 h-20 rounded-full object-cover border-2 border-cc-blue/20" data-testid="sp-photo-img" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-cc-blue/10 text-cc-blue flex items-center justify-center text-xl font-semibold border-2 border-cc-blue/20" data-testid="sp-photo-placeholder">{initials}</div>
        )}
      </div>
      {canEdit && (
        <label className="inline-flex items-center gap-1.5 text-sm text-cc-blue cursor-pointer hover:underline" data-testid="sp-photo-upload">
          <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={onFile} />
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {hasPhoto ? "Change photo" : "Add photo"}
        </label>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, count, children, testId, onEdit, editing, onCancel }) {
  return (
    <Card className="p-0" data-testid={testId}>
      <div className="flex items-center gap-2 px-5 py-3 border-b">
        <Icon className="w-4 h-4 text-cc-blue" />
        <h3 className="font-medium text-zinc-800">{title}</h3>
        {count !== undefined && <span className="ml-auto text-xs text-zinc-400">{count}</span>}
        {onEdit && !editing && <button onClick={onEdit} className="ml-auto text-zinc-400 hover:text-cc-blue" data-testid={`${testId}-edit`}><Pencil className="w-4 h-4" /></button>}
        {editing && onCancel && <button onClick={onCancel} className="ml-auto text-zinc-400 hover:text-red-500" data-testid={`${testId}-cancel`}><X className="w-4 h-4" /></button>}
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="text-zinc-800 break-words flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-zinc-400" />}{value || "—"}</div>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-400 mb-1">{label}</div>
      {children}
    </div>
  );
}

const fmt = fmtDate;

export function StudentProfileView({ studentId, embedded }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [editSection, setEditSection] = useState(null); // 'profile' | 'recruiting' | null
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const canEdit = CAN_EDIT_ROLES.includes(user?.role);

  const load = () => api.get(`/students/${studentId}/profile`).then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || "Could not load profile"));
  useEffect(() => { if (studentId) load(); /* eslint-disable-next-line */ }, [studentId]);

  if (err) return <EmptyState text={err} />;
  if (!data) return <EmptyState text="Loading…" />;
  const { student, account, school, placements, touchpoints, videos, recruiting, messages } = data;
  const done = touchpoints.filter((t) => t.status === "Completed");
  const scheduled = touchpoints.filter((t) => t.status !== "Completed");

  const startEditProfile = () => {
    setForm({
      name: student.name || "", discipline: student.discipline || "", graduation_date: student.graduation_date ? student.graduation_date.slice(0, 10) : "",
      personal_email: student.personal_email || "", school_email: student.school_email || "", cell_phone: student.cell_phone || "",
      address_state: student.address_state || "", address_zip: student.address_zip || "",
      employment_interest: student.employment_interest ? "yes" : "no",
    });
    setEditSection("profile");
  };
  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch(`/students/${student.id}`, {
        name: form.name, discipline: form.discipline || null, graduation_date: form.graduation_date || null,
        personal_email: form.personal_email || null, school_email: form.school_email || null, cell_phone: form.cell_phone || null,
        address_state: form.address_state || null, address_zip: form.address_zip || null,
        employment_interest: form.employment_interest === "yes",
      });
      toast.success("Profile updated"); setEditSection(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const startEditRecruiting = () => {
    setForm({ status: recruiting?.status || "", interested: recruiting?.interested ? "yes" : "no", notes: recruiting?.notes || "" });
    setEditSection("recruiting");
  };
  const saveRecruiting = async () => {
    setSaving(true);
    try {
      await api.patch(`/students/${student.id}/recruiting`, {
        status: form.status || null, interested: form.interested === "yes", notes: form.notes || "",
      });
      toast.success("Recruiting updated"); setEditSection(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="student-profile">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Section icon={User} title="Profile & contact" testId="sp-profile"
          onEdit={canEdit ? startEditProfile : undefined} editing={editSection === "profile"} onCancel={() => setEditSection(null)}>
          {editSection === "profile" ? (
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sp-edit-name" /></EditField>
              <EditField label="Discipline">
                <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                  <SelectTrigger data-testid="sp-edit-discipline"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </EditField>
              <EditField label="Graduation"><Input type="date" value={form.graduation_date} onChange={(e) => setForm({ ...form, graduation_date: e.target.value })} data-testid="sp-edit-graduation" /></EditField>
              <EditField label="Personal email"><Input value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} data-testid="sp-edit-personal-email" /></EditField>
              <EditField label="School email"><Input value={form.school_email} onChange={(e) => setForm({ ...form, school_email: e.target.value })} data-testid="sp-edit-school-email" /></EditField>
              <EditField label="Cell"><Input value={form.cell_phone} onChange={(e) => setForm({ ...form, cell_phone: e.target.value })} data-testid="sp-edit-cell" /></EditField>
              <EditField label="State"><Input value={form.address_state} onChange={(e) => setForm({ ...form, address_state: e.target.value })} data-testid="sp-edit-state" /></EditField>
              <EditField label="Zip"><Input value={form.address_zip} onChange={(e) => setForm({ ...form, address_zip: e.target.value })} data-testid="sp-edit-zip" /></EditField>
              <EditField label="Employment interest">
                <Select value={form.employment_interest} onValueChange={(v) => setForm({ ...form, employment_interest: v })}>
                  <SelectTrigger data-testid="sp-edit-employment"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Interested</SelectItem><SelectItem value="no">Not set / No</SelectItem></SelectContent>
                </Select>
              </EditField>
              <div className="col-span-2 flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setEditSection(null)}>Cancel</Button>
                <Button size="sm" className="bg-cc-blue text-white" onClick={saveProfile} disabled={saving} data-testid="sp-save-profile">{saving ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><PhotoAvatar student={student} canEdit={canEdit} onUploaded={load} /></div>
              <Info label="Name" value={student.name} />
              <Info label="Discipline" value={student.discipline} />
              <Info label="School" value={school?.name} icon={GraduationCap} />
              <Info label="Graduation" value={fmt(student.graduation_date)} />
              <Info label="Personal email" value={student.personal_email || account?.email} icon={Mail} />
              <Info label="School email" value={student.school_email} icon={Mail} />
              <Info label="Cell" value={student.cell_phone} icon={Phone} />
              <Info label="Location" value={[student.address_state, student.address_zip].filter(Boolean).join(" ")} />
              <Info label="Employment interest" value={student.employment_interest ? "Interested" : "Not set / No"} />
              <Info label="Account status" value={account?.account_status} />
            </div>
          )}
        </Section>

        <Section icon={Building2} title="Placement" count={placements.length} testId="sp-placement">
          {placements.length === 0 ? <EmptyState text="No placement requests." /> : (
            <div className="space-y-3">
              {placements.map((p) => (
                <div key={p.id} className="border-b last:border-0 pb-3 last:pb-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{p.discipline} · {p.rotation_level || "—"}</span>
                    <StatusBadge status={p.placement_status} />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{p.facility_snapshot?.facility_name || p.assigned_facility?.name || "No facility yet"}</p>
                  <p className="text-xs text-zinc-400">Start {fmt(p.requested_start_date)} · CI: {p.clinical_instructor?.name || "—"}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={FileCheck2} title="Paperwork & clearance" testId="sp-paperwork">
          {(() => {
            const pw = placements.flatMap((p) => p.paperwork || []);
            return pw.length === 0 ? <EmptyState text="No paperwork assigned." /> : (
              <ul className="space-y-1.5">
                {pw.map((a) => (
                  <li key={a.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{a.name}</span>
                    <StatusBadge status={a.status} />
                  </li>
                ))}
              </ul>
            );
          })()}
        </Section>

        <Section icon={Coffee} title="Touchpoints" count={`${done.length} done · ${scheduled.length} scheduled`} testId="sp-touchpoints">
          {touchpoints.length === 0 ? <EmptyState text="No touchpoints." /> : (
            <div className="space-y-2">
              {scheduled.length > 0 && <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Scheduled / pending</p>}
              {scheduled.map((t) => (
                <div key={t.id} className="text-sm flex items-center justify-between"><span>{t.type}</span><span className="text-xs text-zinc-400">{fmt(t.scheduled_at)}</span></div>
              ))}
              {done.length > 0 && <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold pt-2">Completed</p>}
              {done.map((t) => (
                <div key={t.id} className="text-sm flex items-center justify-between text-zinc-500"><span>{t.type}</span><span className="text-xs">{fmt(t.scheduled_at)}</span></div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={Video} title="Video calls" count={videos.length} testId="sp-videos">
          {videos.length === 0 ? <EmptyState text="No video calls." /> : (
            <ul className="space-y-1.5">
              {videos.map((v) => (
                <li key={v.id} className="flex items-center justify-between text-sm"><span className="truncate">{v.title || "Meeting"}</span><span className="text-xs text-zinc-400">{fmt(v.scheduled_start)}</span></li>
              ))}
            </ul>
          )}
        </Section>

        <Section icon={Briefcase} title="Recruiting" testId="sp-recruiting"
          onEdit={canEdit ? startEditRecruiting : undefined} editing={editSection === "recruiting"} onCancel={() => setEditSection(null)}>
          {editSection === "recruiting" ? (
            <div className="space-y-3">
              <EditField label="Status"><Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} placeholder="e.g. Interested, Interviewing, Hired" data-testid="sp-edit-recruiting-status" /></EditField>
              <EditField label="Interested">
                <Select value={form.interested} onValueChange={(v) => setForm({ ...form, interested: v })}>
                  <SelectTrigger data-testid="sp-edit-recruiting-interested"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select>
              </EditField>
              <EditField label="Recruiter notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="sp-edit-recruiting-notes" /></EditField>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditSection(null)}>Cancel</Button>
                <Button size="sm" className="bg-cc-blue text-white" onClick={saveRecruiting} disabled={saving} data-testid="sp-save-recruiting">{saving ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          ) : !recruiting ? (
            <EmptyState text={canEdit ? "Not in recruiting pipeline. Click the pencil to add." : "Not in recruiting pipeline."} />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Info label="Status" value={recruiting.status} />
              <Info label="Interested" value={recruiting.interested ? "Yes" : "No"} />
              <Info label="Recruiter notes" value={recruiting.notes} />
            </div>
          )}
        </Section>
      </div>

      <Section icon={MessageSquare} title="Recent messages" count={messages.length} testId="sp-messages">
        {messages.length === 0 ? <EmptyState text="No messages." /> : (
          <ul className="space-y-2">
            {messages.map((m) => (
              <li key={m.id} className="text-sm border-b last:border-0 pb-2 last:pb-0">
                <p className="text-zinc-700">{m.body}</p>
                <p className="text-xs text-zinc-400">{fmt(m.created_at)}</p>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <div>
      <PageHeader title="Student" subtitle="Full profile — one pane of glass">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="student-profile-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
      </PageHeader>
      <StudentProfileView studentId={id} />
    </div>
  );
}
